import arrayUtils from '@common/helpers/array.utils';
import * as monaco from 'monaco-editor';
import React, { Fragment } from 'react';
import autoBind from 'react-autobind';
import { OverlayTrigger, Tooltip as TooltipBootstrap } from 'react-bootstrap';
import { getExternalConstants } from './calculo/Implementation/LanguageSyntaxAnalyzer';
import { docsUrl, loadLanguage, validateSyntaxLanguage } from './calculo/Language';
import { confConsole, languageConsole, optionsConsole } from './console.config';
import EditorData from './editor.data';
import { options } from './editor.utils';
import { themes } from './themeList';

class EditorComponent extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      consoleText: undefined,
      foreground: '',
      background: '',
    };

    this.loadThemes();
    this.loadConsoleLanguage();
  }

  /**
   * Carrega temas do Monaco Editor.
   */
  loadThemes() {
    themes.forEach((theme) => {
      monaco.editor.defineTheme(theme.name, theme.theme);
    });
  }

  /**
   * Registra a linguagem utilizada pelo console e configura seus tokens e definições.
   */
  loadConsoleLanguage() {
    if (!monaco.languages.getLanguages().some(({ id }) => id === 'languageConsole')) {
      // Registra a linguagem para o Monaco Editor.
      monaco.languages.register({ id: 'languageConsole' });

      // Define os tokens da linguagem, e como cada tipo é reconhecido, palavras-chave, funções, operadores etc.
      monaco.languages.setMonarchTokensProvider('languageConsole', languageConsole);

      // Define configurações da linguagem, como comentários, fechamento de pares etc.
      monaco.languages.setLanguageConfiguration('languageConsole', confConsole);
    }
  }

  async handleChange(e) {
    const model = this._editor.getModel();
    const text = model.getValue();

    clearTimeout(this.timeout);
    this.timeout = setTimeout(async () => {
      this.props.onChange && this.props.onChange(text);

      this.validateSyntax(text);
      this.timeout = undefined;
    }, 500);
  }

  validateSyntax(text) {
    const { language, supportConstants } = this.props;

    if (language !== 'language') return;

    const model = this._editor.getModel();

    monaco.editor.setModelMarkers(model, 'language', []);
    var errors = validateSyntaxLanguage(text, supportConstants);
    monaco.editor.setModelMarkers(model, 'language', errors);
  }

  handleBlur() {
    this.props.onBlur && this.props.onBlur();
  }

  /**
   * Altera tema dos editores de texto, e define a cor de fundo e do texto para ser utilizado pela divisória entre os editores.
   * Observação: Não é possível alterar o tema de somente um editor.
   * @param {string} themeName Nome do tema
   */
  updateConsoleTheme(themeName) {
    const theme = themes.find((t) => t.name === themeName);

    if (theme) {
      const defaultForeground = theme.theme.base === 'vs-dark' ? '#D4D4D4' : '#000000';
      const defaultBackground = theme.theme.base === 'vs-dark' ? '#1E1E1E' : '#FFFFFF';
      this.setState({
        background: (theme.theme.colors || {})['editor.background'] || defaultBackground,
        foreground: (theme.theme.colors || {})['editor.foreground'] || defaultForeground,
      });
    }
  }

  /**
   * Substitui o texto do editor do console pelo informado.
   * @param {string} value Texto para alteração
   */
  setConsoleText(value) {
    this.setState({ consoleText: value || '' });
    if (this._editorConsole) this._editorConsole.setValue(value || '');
  }

  /**
   * Após o carregamento do componente, inicia a construção dos editores de texto, com base nos componentes em tela destinados.
   */
  componentDidMount() {
    const { value, language, theme, isView, supportConstants, hasError, showConsole } = this.props;

    // Cria o editor de código no componente em tela em `this._node`
    this._editor = monaco.editor.create(this._node, {
      extraEditorClassName: `${hasError ? 'span-input-invalid' : ''}`,
      theme: theme,
      language: language,
      readOnly: isView,
      value: value,
      contextmenu: true,
      dragAndDrop: false,
      scrollbar: {
        alwaysConsumeMouseWheel: false,
      },
      ...options,
    });

    this.updateConsoleTheme(theme);

    this.commandId = this._editor.addCommand(
      0,
      () => {
        window.open(docsUrl);
      },
      ''
    );
    this.commandEditorId = this._editor.addCommand(
      0,
      () => {
        window.open('https://github.com/Vitor-Xavier/CalculationEngine/wiki/Monaco-Editor');
      },
      ''
    );
    EditorData.setItem('supportConstants', supportConstants);
    EditorData.setItem('CodeLensCommand', this.commandId);
    EditorData.setItem('EditorCommand', this.commandEditorId);
    loadLanguage();

    // Chama o controle local de mudança na mudança de texto do editor.
    this._editor.onDidChangeModelContent((e) => this.handleChange(e));
    this._editor.onDidBlurEditorText(() => this.handleBlur());

    // Console
    if (showConsole) {
      this._editorConsole = monaco.editor.create(this._nodeConsole, {
        theme: theme,
        language: 'languageConsole',
        readOnly: true,
        lineNumbers: 'off',
        value: this.state.consoleText,
        dragAndDrop: false,
        scrollbar: {
          alwaysConsumeMouseWheel: false,
        },
        ...optionsConsole,
      });
    }

    // Chama a validação inicial de sintaxe.
    this.validateSyntax(value);
  }

  /**
   * Verifica por alterações no componente, e atualiza o tema, verificação de erro e texto do console, caso sejam alterados.
   * @param {*} prevProps props anteriores a alteração
   */
  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      const model = this._editor.getModel();
      const text = model.getValue();
      if (text !== this.props.value) this._editor.setValue(this.props.value || '');
    }
    if (this.props.theme !== prevProps.theme) {
      monaco.editor.setTheme(this.props.theme);
      this.updateConsoleTheme(this.props.theme);
    }
    if (this.props.hasError !== prevProps.hasError) {
      this._editor.updateOptions({
        extraEditorClassName: `${this.props.hasError ? 'span-input-invalid' : ''}`,
      });
    }
    if (this.props.consoleText !== prevProps.consoleText) {
      this.setConsoleText(this.props.consoleText);
    }
    if (!arrayUtils.compare(this.props.supportConstants, prevProps.supportConstants)) {
      this.validateSyntax(this.props.value, this.props.supportConstants);
      EditorData.setItem('supportConstants', this.props.supportConstants);
    }
  }

  /**
   * Descarta os editores quando o componente vai ser desmontado.
   */
  componentWillUnmount() {
    this._editor && this._editor.dispose();
    this._editorConsole && this._editorConsole.dispose();
  }

  /**
   * Verifica se o usuário atual possui permissão para a rota/url informada.
   * @param {object} action Objeto caso contenha url ou route será verificado a permissão.
   */
  checkPermission(action) {
    if (action.route) return accessControlValidator.checkPermissionsByName(action.route);
    if (action.url) return accessControlValidator.checkPermissionByUrl(action.url);
    return true;
  }

  onExecute(text) {
    const { onExecute, supportConstants } = this.props;
    if (!onExecute) return;

    var externo = getExternalConstants(
      text,
      supportConstants.find((x) => x.Nome === 'Variavel')?.Colunas || [],
      supportConstants.find((x) => x.Nome === 'Roteiro')?.Colunas || []
    );

    onExecute(text, externo.Variavel, externo.Roteiro);
  }

  render() {
    const { height, name, value, hasError, showConsole, onExecute, onClearConsole, consoleButtons } = this.props;
    return (
      <div
        style={{
          border: `${hasError ? '1px solid #763029' : '1px solid #ccc'}`,
          borderRadius: '.25rem',
          padding: '0px 1px',
          margin: '0px -1px',
        }}
      >
        <div
          className="w-100"
          ref={(editor) => (this._node = editor)}
          name={name}
          style={{
            height: height || '600px',
          }}
        />
        {showConsole && (
          <Fragment>
            <div
              style={{
                padding: '8px 4px 8px 12px',
                background: this.state.background,
                color: this.state.foreground,
                borderTop: `1px solid #ccc`,
              }}
            >
              {'Console'}
              <div className="float-right mr-1">
                {onExecute && (
                  <OverlayTrigger
                    key={`top-console-execute`}
                    placement={'top'}
                    overlay={
                      <TooltipBootstrap id={`tooltip-console-execute`}>
                        <div className={'fontTooltip'}>{'Executar'}</div>
                      </TooltipBootstrap>
                    }
                  >
                    <a
                      key={`${name}consoleExecute`}
                      className="ml-2"
                      style={{ background: 'transparent', color: this.state.foreground }}
                      onClick={() => this.onExecute(value)}
                    >
                      <i className="fas fa-play"></i>
                    </a>
                  </OverlayTrigger>
                )}
                {onClearConsole && (
                  <OverlayTrigger
                    key={`top-console-clear`}
                    placement={'top'}
                    overlay={
                      <TooltipBootstrap id={`tooltip-console-clear`}>
                        <div className={'fontTooltip'}>{'Limpar Console'}</div>
                      </TooltipBootstrap>
                    }
                  >
                    <a
                      key={`${name}consoleClear`}
                      className="ml-2"
                      style={{ background: 'transparent', color: this.state.foreground }}
                      onClick={onClearConsole}
                    >
                      <i className="fas fa-eraser"></i>
                    </a>
                  </OverlayTrigger>
                )}
                {consoleButtons &&
                  consoleButtons
                    .filter((item) => this.checkPermission(item))
                    .map((item, i) => (
                      <OverlayTrigger
                        key={`top-${name}-button${i}`}
                        placement={'top'}
                        overlay={
                          <TooltipBootstrap id={`tooltip-${name}button${i}`}>
                            <div className={'fontTooltip'}>{item.tooltip}</div>
                          </TooltipBootstrap>
                        }
                      >
                        <a
                          key={`${name}button${i}`}
                          className="ml-2"
                          style={{ background: 'transparent', color: this.state.foreground }}
                          onClick={item.onClick(value)}
                        >
                          <i className={item.icon}></i>
                        </a>
                      </OverlayTrigger>
                    ))}
              </div>
            </div>
            <div
              className="w-100"
              ref={(cons) => (this._nodeConsole = cons)}
              name={name + 'Console'}
              style={{
                height: '200px',
              }}
            />
          </Fragment>
        )}
      </div>
    );
  }
}

EditorComponent.defaultProps = {
  supportConstants: [],
};

export default EditorComponent;
